import * as actions from '../../materials/actions';
import { ResponsiveAssignment } from '../types';

type LinkedMaterial = ResponsiveAssignment['linkedMaterials'][number];
type MaterialUpdate = Pick<LinkedMaterial, 'id'> &
  Partial<Pick<LinkedMaterial, 'title' | 'updatedAt'>>;

type MaterialAction = {
  type: string;
  status?: string;
  payload: {
    material?: MaterialUpdate;
    section?: MaterialUpdate;
    updatedMaterial?: MaterialUpdate;
    materialId?: string;
    childrenSectionIds?: string[];
  };
};

const syncLinkedMaterials = <
  T extends { assignments: Array<{ linkedMaterials?: LinkedMaterial[] }> }
>(
  state: T,
  action: MaterialAction
): T => {
  const isLiveUpdate =
    action.type === actions.CLASSROOM_MODULE_LIVE_UPDATED_MATERIAL;
  const isLiveDelete =
    action.type === actions.CLASSROOM_MODULE_LIVE_DELETED_MATERIAL;

  if (!isLiveUpdate && !isLiveDelete && action.status !== 'success') {
    return state;
  }

  const { material, section, updatedMaterial, materialId, childrenSectionIds } =
    action.payload;
  const isDelete =
    isLiveDelete ||
    action.type === actions.CLASSROOM_MODULE_DELETE_SECTION ||
    action.type === actions.CLASSROOM_DELETE_FOLDER;
  const deletedIds = new Set([
    isLiveDelete ? material?.id : materialId,
    ...(childrenSectionIds || []),
  ]);
  const update = material || section || updatedMaterial;
  let changed = false;

  const assignments = state.assignments.map((assignment) => {
    if (!assignment.linkedMaterials) return assignment;

    const linkedMaterials = isDelete
      ? assignment.linkedMaterials.filter((link) => !deletedIds.has(link.id))
      : assignment.linkedMaterials.map((link) => {
          if (link.id !== update?.id) return link;
          const title = update.title ?? link.title;
          const updatedAt = update.updatedAt ?? link.updatedAt;
          return title === link.title && updatedAt === link.updatedAt
            ? link
            : { ...link, title, updatedAt };
        });

    if (
      linkedMaterials.length === assignment.linkedMaterials.length &&
      linkedMaterials.every(
        (link, index) => link === assignment.linkedMaterials![index]
      )
    ) {
      return assignment;
    }

    changed = true;
    return { ...assignment, linkedMaterials };
  });

  return changed ? { ...state, assignments } : state;
};

export default syncLinkedMaterials;
